import React, { useState, useCallback } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import withStyles from 'isomorphic-style-loader/withStyles';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import FormControl from 'react-bootstrap/FormControl';
import cx from 'classnames';

import Link from '../../Link';
import CustomPagination from '../../CustomPagination';
import Loader from '../../../components/Common/Loader';

import updateCategoryStatus from '../../../actions/siteadmin/Category/updateCategoryStatus';
import deleteCategory from '../../../actions/siteadmin/Category/deleteCategory';
import debounce from '../../../helpers/debounce';
import messages from '../../../locale/messages';
import getCurrentPage from '../../../helpers/getCurrentPage';
import { demoRestriction } from '../../../actions/demoRestriction';

import { CommonAdminEditIcon, CommonAdminTrashIcon } from '../../Common/Assets/SVGIcon/CommonSvgIcon';

import bt from '../../../components/commonStyle.css';
import s from './CategoryList.css';

function CategoryList({ categoryDetails, intl }) {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);

  const handleKeywordSearch = useCallback(
    debounce((searchList) => {
      categoryDetails.refetch({ currentPage: 1, searchList });
      setCurrentPage(1);
    }),
    []
  );

  const handleDelete = async (id) => {
    const { getAllCategory, refetch } = categoryDetails;
    const isRestrict = await dispatch(demoRestriction({ restrictType: 'category', restrictValue: id }));
    if (isRestrict) return;
    const newPage = await getCurrentPage({ results: getAllCategory?.results, page: currentPage });
    const res = await dispatch(deleteCategory(id, newPage));
    if (res?.status === 200) {
      setCurrentPage(newPage);
      refetch({ currentPage: newPage });
    }
  };

  const handleStatusChange = async (id, fieldName, value) => {
    const isRestrict = await dispatch(demoRestriction({ restrictType: 'category', restrictValue: id }));
    if (isRestrict) return;
    await dispatch(updateCategoryStatus(id, currentPage, fieldName, value));
    categoryDetails.refetch({ currentPage });
  };

  const paginationData = (page) => {
    setCurrentPage(page);
    categoryDetails.refetch({ currentPage: page });
  };

  const { formatMessage } = intl;
  const { getAllCategory, loading } = categoryDetails;

  return (
    <div className="whiteDropdown">
      <div className={s.exportDisplay}>
        <div className={s.searchGroupContainer}>
          <div className={cx(s.searchInput, 'searchInputRTL')}>
            <FormControl
              type="text"
              placeholder={formatMessage(messages.searchOnly)}
              onChange={(e) => handleKeywordSearch(e.target.value)}
              className={bt.formControlInput}
            />
          </div>
        </div>
        <div className={s.exportTextSection}>
          <Link to="/siteadmin/category/add" className={cx(bt.btnPrimary, bt.displayInlineBlock)}>
            {formatMessage(messages.addCategory)}
          </Link>
        </div>
      </div>

      {loading ? (
        <Loader type="circle" />
      ) : (
        <div className='tableOuterContainer'>
          <Table className="tableContainer">
            <thead>
              <tr>
                <th><FormattedMessage {...messages.id} /></th>
                <th><FormattedMessage {...messages.categoryName} /></th>
                <th><FormattedMessage {...messages.createdAt} /></th>
                <th><FormattedMessage {...messages.status} /></th>
                <th><FormattedMessage {...messages.isPopular} /></th>
                <th><FormattedMessage {...messages.action} /></th>
                <th><FormattedMessage {...messages.deleteAction} /></th>
              </tr>
            </thead>
            <tbody>
              {getAllCategory?.results?.length > 0 ? (
                getAllCategory.results.map((item) => (
                  <tr key={item.id}>
                    <td data-label={formatMessage(messages.id)}>{item.id}</td>
                    <td data-label={formatMessage(messages.categoryName)}>{item.name}</td>
                    <td data-label={formatMessage(messages.createdAt)}>{moment(parseInt(item.createdAt)).format('DD-MM-YYYY')}</td>
                    <td data-label={formatMessage(messages.status)}>
                      <div>
                        <select
                          value={item.status}
                          onChange={(e) => handleStatusChange(item.id, 'status', e.target.value)}
                          className={bt.selectInput}
                        >
                          <option value="active">{formatMessage(messages.active)}</option>
                          <option value="inactive">{formatMessage(messages.inactive)}</option>
                        </select>
                      </div>
                    </td>
                    <td data-label={formatMessage(messages.isPopular)}>
                      <div>
                        <select
                          value={item.isPopular ? 'true' : 'false'}
                          onChange={(e) => handleStatusChange(item.id, 'isPopular', e.target.value)}
                          className={bt.selectInput}
                        >
                          <option value="true">{formatMessage(messages.yes)}</option>
                          <option value="false">{formatMessage(messages.no)}</option>
                        </select>
                      </div>
                    </td>
                    <td data-label={formatMessage(messages.action)}>
                      <div>
                        <Link to={`/siteadmin/category/edit/${item.id}`} className={s.noLink}>
                          <CommonAdminEditIcon className={cx(s.editIcon, 'editIconRTL')} />
                          <span className={s.vtrMiddle}>
                            <FormattedMessage {...messages.editAction} />
                          </span>
                        </Link>
                      </div>
                    </td>
                    <td data-label={formatMessage(messages.deleteAction)}>
                      <div>
                        <Button onClick={() => handleDelete(item.id)} className={s.iconBtn}>
                          <CommonAdminTrashIcon className={cx(s.editIcon, 'editIconRTL')} />
                          <span className={s.vtrMiddle}>
                            <FormattedMessage {...messages.deleteAction} />
                          </span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className={'tableNoRecordCel'}>
                    <FormattedMessage {...messages.noResult} />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      {getAllCategory?.results?.length > 0 && (
        <div className={cx(bt.space5, bt.spaceTop5)}>
          <CustomPagination
            total={getAllCategory?.count}
            currentPage={currentPage}
            defaultCurrent={1}
            defaultPageSize={10}
            change={paginationData}
            paginationLabel={formatMessage(messages.categorieslist)}
          />
        </div>
      )}
    </div>
  );
}

CategoryList.propTypes = {
  categoryDetails: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired
};

export default injectIntl(withStyles(bt, s)(CategoryList));
